import React, { useEffect, useState, useRef } from "react";
import { fetchAuth } from "../common/api";
import { useTheme } from "../common/ThemeContext";
import { Play, Pause } from "lucide-react";
import DatePicker from "react-datepicker";
import { useLayoutEffect } from "react";
import SessionPickerModal  from "./SessionPickerModal";
import { formatLocalDate } from "../common/datetimeUtils";

const OverviewSession = () => {
  const { isDarkMode } = useTheme();
  const [sequences, setSequences] = useState([]);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [players, setPlayers] = useState([]);
  const [timestampIndex, setTimestampIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const [maxTimestamp, setMaxTimestamp] = useState(0);
  const [visiblePlayers, setVisiblePlayers] = useState({});
  const [sessionImage, setSessionImage] = useState(null);
  const [sessionNotes, setSessionNotes] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fieldWidthMeters, setFieldWidthMeters] = useState(800);
  const [fieldHeightMeters, setFieldHeightMeters] = useState(500);
  const fieldRef = useRef(null);
  const [xMin, setXMin] = useState(0);
  const [xMax, setXMax] = useState(1);
  const [yMin, setYMin] = useState(0);
  const [yMax, setYMax] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPeriodModal, setShowPeriodModal] = useState(false);


  useLayoutEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setFieldWidthPx(width);
        setFieldHeightPx(height);
      }
    });
  
    if (fieldRef.current) {
      observer.observe(fieldRef.current);
    }
  
    return () => observer.disconnect();
  }, []);

  const fetchPositions = async (sequenceObj) => {
    setIsLoading(true);
    const { sequence, date, period } = sequenceObj;
    let url = `/overview_session_positions?sequence=${sequence}&date=${date}&period=${period}`;
  const res = await fetchAuth(url);
  const data = await res.json();
  if (data && data.players) {
    const allPoints = data.players.flatMap((p) => p.points || []);
    if (!allPoints.length) {
      setPlayers([]);
      setMaxTimestamp(0);
      return;
    }

    const xMin = Math.min(...data.players.map((p) => p.xCoordsMapMin));
    const xMax = Math.max(...data.players.map((p) => p.xCoordsMapMax));
    const yMin = Math.min(...data.players.map((p) => p.yCoordsMapMin));
    const yMax = Math.max(...data.players.map((p) => p.yCoordsMapMax));
    setFieldWidthMeters(xMax - xMin);
    setFieldHeightMeters(yMax - yMin);
    const xMinValue = Math.min(...data.players.map((p) => p.xCoordsMapMin));
    const xMaxValue = Math.max(...data.players.map((p) => p.xCoordsMapMax));
    const yMinValue = Math.min(...data.players.map((p) => p.yCoordsMapMin));
    const yMaxValue = Math.max(...data.players.map((p) => p.yCoordsMapMax));
    
    setXMin(xMinValue);
    setXMax(xMaxValue);
    setYMin(yMinValue);
    setYMax(yMaxValue);
    
    const normalizedPlayers = data.players.map((player) => {
        const rawPoints = player.points.map((pt) => ({
          x: (pt.x - xMinValue) / (xMaxValue - xMinValue), // entre 0 et 1
          y: (pt.y - yMinValue) / (yMaxValue - yMinValue), // entre 0 et 1
          t: pt.t,
          fatigue: typeof pt.fatigue === "number" ? pt.fatigue * 100 : null,
        }));
      
        return {
          ...player,
          points: interpolatePoints(rawPoints, 10),
        };
      });
      
      
      
    setPlayers(normalizedPlayers);
    setTimestampIndex(0);
    setVisiblePlayers(Object.fromEntries(normalizedPlayers.map(p => [p.player_id, true])));
    setSessionImage(data.image || null);
    setSessionNotes(data.notes || []);
    const maxT = Math.max(...allPoints.map((pt) => pt.t));
    setMaxTimestamp(normalizedPlayers [0]?.points.length - 1 || 0);

    setIsPlaying(true); // ▶️ auto-play
  }
  setIsLoading(false);
};
  
  const loadSequences = async (dateStr = null) => {
    if (!selectedSession?.period) return;
    let url = `/overview_session_sequences`;
    if (dateStr) url += `&date=${dateStr}`;
    if (selectedSession?.period) url += `&period=${selectedSession.period}`;
  
    const res = await fetchAuth(url);
    const data = await res.json();
  
    if (data && data.grouped_periods?.length > 0) {
      const enriched = data.grouped_periods.map((g) => ({
        id: `${g.name}_${g.start_ts}`,
        name: g.name,
        start_ts: g.start_ts,
        end_ts: g.end_ts,
      }));
  
      const firstSequence = {
        sequence: enriched[0].id,
        date: selectedSession.date,
        period: selectedSession.period,
        type: data.type,
      };
  
      setSequences(enriched);
      setSelectedSequence(firstSequence);
  
      // ✅ On enchaîne ici directement
      fetchPositions(firstSequence);
    }
  };
  
  

  
  useEffect(() => {
    const loadAvailableDates = async () => {
      const res = await fetchAuth(`/available_simulation_dates`);
      const data = await res.json();
  
      if (Array.isArray(data) && data.length > 0) {
        setAvailableDates(data);
  
        // 🧠 Choisir la session la plus récente (par date + start_ts)
        const sorted = [...data].sort((a, b) => {
          const tsA = new Date(a.date).getTime() + (a.start_ts || 0);
          const tsB = new Date(b.date).getTime() + (b.start_ts || 0);
          return tsB - tsA; // desc
        });
  
        const mostRecent = sorted[0];
        setSelectedDate(new Date(mostRecent.date));
        setSelectedSession(mostRecent);
      }
    };
    }, []);
  
  function interpolatePoints(points, factor = 10) {
    if (points.length < 2) return points;
  
    const interpolated = [];
  
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
  
      interpolated.push(p1);
  
      for (let j = 1; j < factor; j++) {
        const t = j / factor;
  
        interpolated.push({
          x: p1.x + (p2.x - p1.x) * t,
          y: p1.y + (p2.y - p1.y) * t,
          t: p1.t + (p2.t - p1.t) * t,
          fatigue:
            (p1.fatigue != null && p2.fatigue != null)
              ? parseFloat((p1.fatigue + (p2.fatigue - p1.fatigue) * t).toFixed(1))
              : null,
        });
      }
    }
  
    interpolated.push(points[points.length - 1]);
    return interpolated;
  }
  
  
  const getFatigueBorderColor = (fatigue) => {
    if (fatigue == null) return "border-gray-400";
    if (fatigue < 20) return "border-green-500";
    if (fatigue < 50) return "border-yellow-400";
    if (fatigue < 75) return "border-orange-500";
    return "border-red-600";
  };
  
  useEffect(() => {
    if (selectedSession) {
      loadSequences(selectedSession.date); // avec date et selectedSession.period dans loadSequences
    }
  }, [selectedSession]);
  



  useEffect(() => {
    if (isPlaying) {
        intervalRef.current = setInterval(() => {
            setTimestampIndex((prev) =>
              prev < maxTimestamp ? prev + 1 : prev
            );
          }, 100); // 10 Hz
          
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, maxTimestamp]);
  

  const togglePlay = () => setIsPlaying((prev) => !prev);

  const formatTime = (index) => {
    const totalSeconds = index / 10; // Because we run at 10Hz
    const mins = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = Math.floor(totalSeconds % 60).toString().padStart(2, "0");
    const dec = Math.floor((totalSeconds * 10) % 10); // for tenths of seconds
    return `${mins}:${secs}.${dec}`;
  };

  function formatDuration(durationSeconds) {
    const mins = Math.floor(durationSeconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (durationSeconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  }
 
  
  const sessionsByDate = availableDates.reduce((acc, session) => {
    const d = session.date;
    if (!acc[d]) acc[d] = [];
    acc[d].push(session);
    return acc;
  }, {});

  const handleSelectSession = (session) => {
    if (session) {
      setSelectedSession(session);
    }
    setShowPeriodModal(false);
  };
  
  
  useEffect(() => {
    if (selectedSession) {
      loadSequences(selectedSession.date);
    }
  }, [selectedSession]);
  
  return (
<div className="container mx-auto p-4">
{isLoading ? (
        <div className="flex justify-center items-center h-[500px]">
            Loading session data...<br/>
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
        </div>
      ) : (
<div className={`p-4 ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"}`}>
<div className="flex justify-between items-center mb-4">
  <div><div className="mb-4">
  <DatePicker
    selected={selectedDate}
    onChange={(date) => {
        setSelectedDate(date);
        const key = date.toISOString().split("T")[0];
        const sessions = sessionsByDate[key];
      
        if (sessions?.length === 1) {
          setSelectedSession(sessions[0]);
        } else if (sessions?.length > 1) {
          setShowPeriodModal(true);
        }
      }}
      
      
      includeDates={Object.keys(sessionsByDate).map((d) => new Date(d))}
    dateFormat="dd/MM/yyyy"
    className={`p-2 border rounded-md shadow-md cursor-pointer transition text-sm 
      ${isDarkMode ? 'bg-white text-black border-gray-600 hover:bg-gray-700' : 'bg-white text-black border-gray-300 hover:bg-gray-100'}`}
    placeholderText="Select a session date"
    showMonthDropdown
    showYearDropdown
    dropdownMode="select"
  />
</div>
</div>

{showPeriodModal && selectedDate && (
    <SessionPickerModal
  isOpen={showPeriodModal}
  sessions={
    Object.values(
      Object.fromEntries(
        sessionsByDate[selectedDate.toISOString().split("T")[0]]?.map((s) => [
          `${s.date}_${s.period}`, // unique key
          s,
        ]) || []
      )
    )
  }
  onSelect={handleSelectSession}
/>
)}

  {/* Centre : Type avec majuscule */}
  <h2 className="text-xl font-bold text-center">
    {selectedSequence?.type
      ? `${selectedSequence.type.charAt(0).toUpperCase()}${selectedSequence.type.slice(1)} Overview`
      : "Session Overview"}
  </h2>

  {/* Droite : Date + Morning/Afternoon */}
  <div className="text-sm text-gray-500 text-right">
    {selectedSequence?.date && (
      <>
        {formatLocalDate(selectedSequence.date, {
          locale: "en-US",
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
        {selectedSequence.period === "M" ? ", Morning" : ", Afternoon"}
      </>
    )}
  </div>
</div>
<select
  value={selectedSequence?.sequence || ""}
  onChange={(e) => {
    const selected = sequences.find((s) => s.id === e.target.value);
    if (selected) {
      const newSequence = {
        sequence: selected.id,
        date: selectedSession.date,
        period: selectedSession.period,
        type: selectedSequence?.type || "session",
      };
      setSelectedSequence(newSequence);
      fetchPositions(newSequence);
    }
  }}
  className="mb-4 text-slate-950 p-2 border rounded"
>
    {sequences.map((seq) => (
        <option key={seq.id} value={seq.id}>
        {`${seq.name} (${new Date(seq.start_ts * 1000).toLocaleTimeString()} / Duration : ${formatDuration(seq.end_ts - seq.start_ts)})`}
        </option>
    ))}
    </select>


    <div className="flex flex-col lg:flex-row gap-6">
    {/* LEFT SIDE */}
    <div className="w-full lg:w-1/3 flex flex-col items-center">
    <img
      src={sessionImage ? `data:image/jpeg;base64,${sessionImage}` : "/no_picture.png"}
      alt="Session"
      className="w-full h-auto object-cover rounded border mb-4"
    />
<div
  className={`w-full p-2 rounded ${
    isDarkMode ? "bg-gray-800 text-white" : "bg-gray-100 text-black"
  }`}
>
  <h3 className="font-semibold mb-2">📝 Notes</h3>
  {sessionNotes.length > 0 ? (
    <ul className="list-disc list-inside text-sm">
      {sessionNotes.map((note, i) => (
        <li key={i}>{note}</li>
      ))}
    </ul>
  ) : (
    <p className={`text-sm italic ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
      No notes entered.
    </p>
  )}
</div>

  </div>

  {/* RIGHT SIDE */}
  <div className="w-full lg:w-2/3 flex flex-col">
  <div className="flex flex-col lg:flex-row">
  {/* TERRAIN */}
      <div
        ref={fieldRef}
        className="relative border border-green-600 bg-green-700 w-full aspect-[16/10] min-h-[200px]"
        >

        {players.map((player) => {
          if (
            !visiblePlayers[player.player_id] ||
            !player.points ||
            player.points.length <= timestampIndex
          ) return null;

          const currentPoint = player.points[timestampIndex];
          if (!currentPoint) return null;
          const { x, y } = currentPoint;
          const realX = fieldRef.current?.offsetWidth * currentPoint.x;
          const realY = fieldRef.current?.offsetHeight * currentPoint.y;                
          
          return (
            <div
            key={player.player_id}
            className={`absolute w-6 h-6 rounded-full border-2 bg-black/40 text-white text-xs flex items-center justify-center overflow-hidden ${getFatigueBorderColor(currentPoint.fatigue)}`}
            style={{
                left: `${realX}px`,
                top: `${realY}px`,
                transform: "translate(-50%, -50%)",
              }}                         
                title={`${player.name} - 💢 Mechanical fatigue: ${currentPoint.fatigue != null ? currentPoint.fatigue.toFixed(1) : 'N/A'}%`}
                >
              {player.profile_picture ? (
                <img
                  src={`data:image/jpeg;base64,${player.profile_picture}`}
                  alt={player.name}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <span className="font-semibold">{player.name.split(" ").map(n => n[0]).join("")}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* PLAYER TOGGLE */}
      <div className="mt-4 lg:mt-0 lg:ml-4 w-full lg:w-auto">
  <h3 className="text-sm font-semibold mb-2">🧍 Players</h3>
  <ul className="flex flex-wrap gap-4 text-sm">
    {players.map((player) => (
      <li key={player.player_id} className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={visiblePlayers[player.player_id] || false}
          onChange={() =>
            setVisiblePlayers((prev) => ({
              ...prev,
              [player.player_id]: !prev[player.player_id],
            }))
          }
        />
        <span>{player.name}</span>
      </li>
    ))}
  </ul>
</div>
    </div>
          {/* Controls */}
          <div className="flex flex-wrap items-center mt-4 gap-4">
          <button
          className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2"
          onClick={togglePlay}
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          {isPlaying ? "" : ""}
        </button>

        <input
            type="range"
            min={0}
            max={maxTimestamp}
            value={timestampIndex}
            onChange={(e) => setTimestampIndex(parseInt(e.target.value))}
            className="w-1/2"
            />

        <span className="text-sm text-gray-500">{formatTime(timestampIndex)}</span>
      </div>
  </div>
</div>



    </div>      )}</div>
  );
};

export default OverviewSession;
